/*
 * Copyright (c) 2026.
 * Spin in het Web B.V. Apeldoorn.
 * Deze software mag alleen gebruikt / bekeken / gewijzigd worden volgens de voorwaarden die zijn vastgelegd in een bijbehorende schriftelijke overeenkomst met Spin in het Web B.V.
 */

/**
 * Auteur: Jelmer Jellema - Spin in het Web B.V
 * Gegenereerd met Claude Code (AI), 26-07-2026
 *
 * De bouwsuggestie-engine: runt en beheert de declaratieve regels rond bouwsuggesties en de
 * registratie van de bouwsuggestie-weergaven (`dl-bouwsuggestie`). Port van de ng1-factory
 * `bouwsuggestieEngine` (`app/states/canvas/bouwsuggestie_engine.js`, golf 8b van stap 8); de
 * regelset en de matcher zijn regel-voor-regel overgenomen. De trigger `run` (bij een modelwijziging,
 * een vers document en een sync), de replay-guard en de log-/undo-/save-plumbing eromheen zitten in
 * de samenwerk-baas (`runBouwsuggesties`, `bouwsuggestieActie`). De regelstatus-data blijft in
 * `node.data('bs')` en reist dus gewoon mee in het opgeslagen model.
 */
import {inject, Injectable, signal} from '@angular/core';
import {TranslateService} from '@ngx-translate/core';
import type {Core, CollectionReturnValue, NodeSingular} from 'cytoscape';
import {KeyedTimeout, Sihwlog} from 'sihw-ngx';
import {elements} from 'dynalearn-common/lib/elements';
import {GlobalModelcanvasService} from '../model/global-modelcanvas.service';
import {DlModel} from '../model/dl-model';
import type {Feedbackherkomst} from '../../lib/feedbackherkomst';
import {Melding} from '../../lib/melding';

/**
 * Eén bouwsuggestie-regel uit de regelset. `match` is een functie of een vaste boolean; een
 * functie geeft false terug als er geen match is, true bij een match zonder args, en anders één
 * of meer node-id-args die in {{arg2}} en verder in de teksten kunnen komen.
 */
export interface Regel {
    handle: string;
    types: string[];
    soort: 'hard' | 'noodzakelijk' | 'gewenst';
    match: boolean | ((engine: BouwsuggestieEngineService, node: NodeSingular, nodedata: BsNodeData, def: {nodename: string}) => boolean | string | string[]);
    /** minimale modelleerniveau */
    minlevel?: number;
    /** maximale modelleerniveau */
    maxlevel?: number;
    /** als true, dan moet de gebruiker ja antwoorden. Als false, dan kunnen we het zelf afleiden (en slaan we ja als nee op) */
    ack?: boolean;
    /** als true, dan kan de regel niet geackt worden (ack moet dus false zijn) en zijn er alleen de knoppen sluit en nee */
    bevestig_gezien?: boolean;
    /** uitgeschakelde oude match, bewaard ter referentie */
    xmatch?: (engine: BouwsuggestieEngineService, node: NodeSingular, nodedata: BsNodeData) => boolean;
}

/**
 * Eén regel-match voor een node: welke regel matcht, met welke argument-node-ids.
 */
export interface RegelInfo {
    handle: string;
    regel: Regel;
    args: string[];
    /**
     * Of deze regel er bij de laatste run bíj kwam. Hij hoort in de lijst en niet in een boekhouding
     * ernaast: dan valt er niets te synchroniseren tussen het moment waarop de run klaar is en het
     * moment waarop een lampje ernaar vraagt.
     */
    isNieuw: boolean;
}

/**
 * De node-data-velden waar de regels naar kijken, plus de `bs`-regelstatus-administratie.
 */
export interface BsNodeData {
    id?: string;
    type?: string;
    label?: string;
    from?: string;
    to?: string;
    parentId?: string;
    bs?: {rs?: Record<string, 2 | 3>};
}

/**
 * Een geregistreerde bouwsuggestie-weergave (`dl-bouwsuggestie`): de node plus de callbacks
 * waarmee de engine een run of een andere geopende dialoog meldt.
 */
interface BouwsuggestieWeergave {
    node: NodeSingular;
    updateNaRun: () => void;
    andereOpen: () => void;
}

/**
 * De draagbare uitkomst van een engine-run voor één modelnode, zoals die over de samenwerklijn
 * reist (het `bsSync`-samenwerkbericht): de `bs`-regelstatus-administratie plus de gematchte
 * regels als handle + args. De volger zoekt het regel-object zelf terug op de handle.
 */
export interface BsSyncNode {
    id: string;
    bs: BsNodeData['bs'];
    /** Per gematchte regel de handle, de args en of hij bij de leider nieuw was. */
    regels: Array<{handle: string; args: string[]; isNieuw: boolean}>;
}

@Injectable({providedIn: 'root'})
export class BouwsuggestieEngineService {

    readonly log = new Sihwlog('info', 'BouwsuggestieEngine');

    /**
     * Permanente eigenaar van de model-`cy`.
     */
    private readonly modelcanvas = inject(GlobalModelcanvasService);

    /**
     * Het modeldocument, voor het effectieve modelleerniveau in de regel-filtering.
     */
    private readonly dlModel = inject(DlModel);

    private readonly translateService = inject(TranslateService);

    /**
     * We bewaren de gefilterde en gesorteerde regels gewoon op node-id in de service. De
     * bouwsuggestie-weergaven kunnen ze opvragen.
     */
    private readonly regelsPerNode = new Map<string, RegelInfo[]>();

    /**
     * Welke regel bij welke node stond na de vorige run, als `nodeId|handle`; `null` zolang er voor
     * dit model nog geen run geweest is. Tegen `null` is niets nieuw — dat is het antwoord op het
     * laden van een model, en het is een datavraag in plaats van een klok.
     */
    private vorigeRegelsleutels: Set<string> | null = null;


    /**
     * Alle geregistreerde bouwsuggestie-weergaven.
     */
    private readonly weergaven: BouwsuggestieWeergave[] = [];

    /**
     * De totalen per soort over het hele model, voor de tellers op de feedbackbalk (golf f1).
     * Vers object per hertelling, zodat het signal bumpt.
     */
    private readonly _totalen = signal<{noodzakelijk: number; gewenst: number}>({noodzakelijk: 0, gewenst: 0});

    /**
     * De totalen per soort over het hele model. Vervangt de ng1-optelling over
     * `node.data('bs_data')`, die elke digest opnieuw telde; deze wordt herteld na elke run en na
     * een regelstatuswijziging.
     */
    readonly totalen = this._totalen.asReadonly();

    /**
     * Of er bij de laatste run per soort iets nieuws bij kwam. Bewust een vers object per run, ook
     * als er niets nieuws is: de feedbackbalk hangt aan de identiteit van dit signal en meldt dus
     * één keer per run. Een boolean zou bij twee runs mét nieuws achter elkaar geen flank geven.
     */
    private readonly _nieuwsPerSoort = signal<{noodzakelijk: boolean; gewenst: boolean}>(
        {noodzakelijk: false, gewenst: false});

    readonly nieuwsPerSoort = this._nieuwsPerSoort.asReadonly();

    /**
     * De run-debounce ('run') plus de wachters op de eerstvolgende echte run, en de herkomsten die
     * de wachtende aanroepers meegaven.
     */
    private readonly runTimer = new KeyedTimeout();
    private runWachters: Array<() => void> = [];
    private runHerkomsten: Feedbackherkomst[] = [];

    /**
     * Er is een run klaar; zie `bijRunKlaar`.
     */
    private readonly runKlaarMelding = new Melding<void>();

    /**
     * De actieve model-`cy`, telkens vers uit de canvas-service — de regels en helpers lezen
     * `engine.cy`, net als in de ng1-versie.
     */
    get cy(): Core {
        return this.modelcanvas.getCy() as Core;
    }

    constructor() {
        this.log.debug('BouwsuggestieEngine');
        //onetime classinit: uitwerken van de regel-types met al hun subtypes
        //de regels staan al op volgorde, per definitie
        for (const regel of this.regels) {
            const uitgewerkteTypes: string[] = [];
            for (const type of regel.types) {
                if (!uitgewerkteTypes.includes(type)) {
                    uitgewerkteTypes.push(type);
                }
                for (const subtype of elements.alleSubs(type)) {
                    if (!uitgewerkteTypes.includes(subtype)) {
                        uitgewerkteTypes.push(subtype);
                    }
                }
            }
            regel.types = uitgewerkteTypes; //vervang door uitgewerkte versie
        }
        this.log.debug(`Uitgewerkte regels`, this.regels);
    }

    /**
     * Een bouwsuggestie-weergave registreert zichzelf. Geeft een handle terug om zelf een
     * dialoog-open te melden (de anderen kijken dan of ze willen sluiten) en om te ontkoppelen.
     */
    registreer(node: NodeSingular, callbacks: {updateNaRun: () => void; andereOpen: () => void}): {meldDialoogGeopend: () => void; ontkoppel: () => void} {
        const weergave: BouwsuggestieWeergave = {node, ...callbacks};
        this.weergaven.push(weergave);
        //updaten alsof we een run hebben gehad
        weergave.updateNaRun();
        return {
            meldDialoogGeopend: () => {
                for (const andere of this.weergaven) {
                    if (andere !== weergave) {
                        andere.andereOpen();
                    }
                }
            },
            ontkoppel: () => {
                const index = this.weergaven.indexOf(weergave);
                if (index >= 0) {
                    this.log.debug(`Ontkoppel weergave`, weergave.node.id());
                    this.weergaven.splice(index, 1);
                }
            }
        };
    }

    /**
     * Geeft de gesorteerde regelinfo terug voor de opgegeven node.
     */
    regelinfosVoor(node: NodeSingular | undefined): RegelInfo[] {
        return (node && this.regelsPerNode.get(node.id())) || [];
    }

    /**
     * Zet op elke regel van deze run of hij er bíj kwam, en onthoudt de nieuwe ijklijn. Wie de
     * uitkomst van een ander overneemt komt hier niet langs — die neemt diens antwoord verbatim over
     * (`importeerRun`). Zonder voorganger is er niets nieuw: dat is een net geladen model.
     */
    private bepaalNieuweRegels(herkomst: Feedbackherkomst): void {
        const sleutels = new Set<string>();
        const nieuws = {noodzakelijk: false, gewenst: false};
        for (const [nodeId, regels] of this.regelsPerNode) {
            for (const regelinfo of regels) {
                const sleutel = `${nodeId}|${regelinfo.handle}`;
                sleutels.add(sleutel);
                regelinfo.isNieuw = herkomst === 'berekend' && !!this.vorigeRegelsleutels
                    && !this.vorigeRegelsleutels.has(sleutel);
                if (regelinfo.isNieuw) {
                    nieuws[regelinfo.regel.soort === 'gewenst' ? 'gewenst' : 'noodzakelijk'] = true;
                }
            }
        }
        this.vorigeRegelsleutels = sleutels;
        this._nieuwsPerSoort.set(nieuws);
    }

    /**
     * Zet de engine terug op de stand van een vers model: geen regels, geen ijklijn en geen nieuws.
     * Zonder dit vergelijkt een net geladen model met de regels van het vórige, en dan is bij een
     * ander model álles nieuw. Aangeroepen door `OpslagService.reset()`, naast die van de norm.
     */
    reset(): void {
        this.regelsPerNode.clear();
        this.vorigeRegelsleutels = null;
        this._nieuwsPerSoort.set({noodzakelijk: false, gewenst: false});
        this._totalen.set({noodzakelijk: 0, gewenst: 0});
    }

    /**
     * Geeft de regelgroepen terug voor de opgegeven node: noodzakelijk (incl. 'hard') en gewenst.
     * Dit maakt het onderscheid al in de engine, zodat de weergave niet zelf hoeft te splitsen.
     */
    regelgroepenVoor(node: NodeSingular | undefined): {noodzakelijk: RegelInfo[]; gewenst: RegelInfo[]} {
        const lijst = this.regelinfosVoor(node);
        const noodzakelijk: RegelInfo[] = [];
        const gewenst: RegelInfo[] = [];
        for (const ri of lijst) {
            switch (ri?.regel?.soort) {
                case 'hard':
                case 'noodzakelijk':
                    noodzakelijk.push(ri);
                    break;
                case 'gewenst':
                    gewenst.push(ri);
                    break;
            }
        }
        return {noodzakelijk, gewenst};
    }

    /**
     * De soort van een regel op zijn handle, of `null` als die regel hier niet (meer) bestaat. Voor
     * de replay: een bewaarde aanwijzing draagt alleen de handle en de node, en de weergave splitst
     * op soort. `hard` telt daarbij als noodzakelijk, net als in `regelgroepenVoor`.
     */
    soortVanRegel(handle: string): 'noodzakelijk' | 'gewenst' | null {
        const soort = this.regels.find(regel => regel.handle === handle)?.soort;
        switch (soort) {
            case 'gewenst':
                return 'gewenst';
            case 'hard':
            case 'noodzakelijk':
                return 'noodzakelijk';
            default:
                return null;
        }
    }

    /**
     * Run de engine op de huidige cy (debounced 0,1s). De promise resolvet zodra de run gedaan
     * is; anders dan de ng1-`$timeout`-variant resolven ook de aanroepen waarvan de debounce
     * werd ingehaald (die bleven voorheen hangen — de vervolgsave kwam dan van de nieuwere
     * aanroep). De replay-guard ligt bij de trigger, `runBouwsuggesties` in de samenwerk-baas.
     *
     * De herkomst wordt net als de wachters opgespaard en in dezelfde timeruitvoering verbruikt.
     * Coalesceren twee aanroepen binnen de debounce, dan **wint `berekend`**: er zat een echte
     * modelwijziging bij, en dan is dat het antwoord. Zonder die regel zou de laatste aanroeper
     * beslissen, en dan wist een sync het nieuws van een net gedane bewerking uit.
     * @param [herkomst] waar de uitkomst van deze run vandaan komt
     */
    run(herkomst: Feedbackherkomst = 'berekend'): Promise<void> {
        this.log.debug('BouwsuggestieEngine run', herkomst);
        return new Promise(resolve => {
            this.runWachters.push(resolve);
            this.runHerkomsten.push(herkomst);
            this.runTimer.set('run', () => {
                const herkomsten = this.runHerkomsten;
                this.runHerkomsten = [];
                //de wachters resolven áltijd, ook als de run zelf faalt: aan deze promise hangt
                //(via runBouwsuggesties) de opslagronde van de samenwerk-baas, en die mag hier nooit
                //op blijven hangen
                try {
                    this.runNu(herkomsten.includes('berekend') ? 'berekend' : 'teruggehaald');
                } catch (e) {
                    this.log.error('BouwsuggestieEngine: run mislukt', e);
                } finally {
                    const wachters = this.runWachters;
                    this.runWachters = [];
                    for (const wachter of wachters) {
                        wachter(); //we hebben gerund
                    }
                    this.runKlaarMelding.meld();
                }
            }, 100);
        });
    }

    /**
     * Of er een run gepland staat of bezig is: van de aanroep van `run` tot het moment waarop zijn
     * wachters resolven. De opslagketen leest dit als voorwaarde voor rust; een snapshot die eerder
     * gemaakt wordt, draagt de bouwsuggesties van vóór de wijziging.
     */
    get runGepland(): boolean {
        return this.runWachters.length > 0;
    }

    /**
     * Abonneert op het einde van een run. De melding komt direct na het resolven van de wachters; wat
     * daarop wacht (de opslagronde en de save erachter) loopt pas in de microtaken daarna.
     */
    bijRunKlaar(luisteraar: () => void): void {
        this.runKlaarMelding.abonneer(luisteraar);
    }

    /**
     * Interne, ongedebouncete uitvoering van de engine-run.
     */
    private runNu(herkomst: Feedbackherkomst): void {
        this.log.debug('BouwsuggestieEngine run NU');
        if (!this.modelcanvas.getCy()) {
            throw new Error('BouwsuggestieEngine: cy is niet geset');
        }
        //per node afgeschermd: een falende match-functie op één node mag de rest van de run
        //(en de hertelling) niet meenemen
        for (const node of this.cy.nodes('.model').toArray()) {
            try {
                this.runVoorNode(node);
            } catch (e) {
                this.log.error(`BouwsuggestieEngine: runVoorNode mislukt voor ${node.id()}`, e);
            }
        }
        this.bepaalNieuweRegels(herkomst);
        this.hertelTotalen();
        //meld het aan de weergaven
        this.meldWeergavenNaRun();
    }

    /**
     * Meldt alle geregistreerde weergaven een afgeronde run, elk afgeschermd: een falende
     * weergave-callback mag de andere weergaven en de aanroepende keten niet meenemen.
     */
    private meldWeergavenNaRun(): void {
        for (const weergave of this.weergaven) {
            try {
                weergave.updateNaRun();
            } catch (e) {
                this.log.error(`BouwsuggestieEngine: updateNaRun mislukt voor weergave ${weergave.node?.id()}`, e);
            }
        }
    }

    /**
     * Hertelt de totalen per soort over alle modelnodes. Loopt bewust over `cy.nodes('.model')` en
     * niet over `regelsPerNode`: die map houdt entries van verwijderde nodes vast.
     */
    private hertelTotalen(): void {
        let noodzakelijk = 0;
        let gewenst = 0;
        for (const node of this.cy.nodes('.model').toArray()) {
            const groepen = this.regelgroepenVoor(node);
            noodzakelijk += groepen.noodzakelijk.length;
            gewenst += groepen.gewenst.length;
        }
        this._totalen.set({noodzakelijk, gewenst});
    }

    /**
     * Run de engine voor één node.
     */
    runVoorNode(node: NodeSingular): void {
        const nodedata = node.data() as BsNodeData;
        if (!(nodedata?.id && nodedata?.type)) {
            return; //irrelevante
        }

        this.log.debug(`runVoorNode`, nodedata.label);
        //de ng1-elementService vertaalde nodename in getDefinition; de kale elements-lib geeft de
        //rauwe sleutel terug, dus hier zelf vertalen (op een kopie, om de gedeelde definitie niet
        //te muteren) — de naam-regels vergelijken het (vertaalde) label met def.nodename
        const rauweDef = elements.getDefinition(nodedata.type) as {nodename?: string | false};
        const def = {
            ...rauweDef,
            nodename: rauweDef.nodename ? this.translateService.instant(rauweDef.nodename) : rauweDef.nodename
        } as {nodename: string};
        let suggestiedata = nodedata.bs; //beetje stomme naam, maar scheelt opslagruimte tov 'bouwsuggestie'
        //suggestiedata bestaat nu alleen nog uit de stats per regel, maar ruimte voor meer
        if (!suggestiedata) {
            suggestiedata = {};
        }
        //we vullen hem eventjes keurig
        //regstat
        suggestiedata.rs ||= {}; //per handle een status 2 of 3. 2 = bevestigd, 3 = ontkend. Als het er niet in zit, is het 1 (onbeantwoord)

        //we slaan de suggestietaal zelf niet op bij het element, dat moet maar aan deze engine gevraagd worden

        /**
         * Handles die relevant zijn bij deze node, met argumenten.
         */
        const relevanteregels: RegelInfo[] = [];

        //vind de relevante regels
        for (const regel of this.regels) {
            if (!regel.types.includes(nodedata.type)) {
                continue; //niet relevant
            }

            //juiste modelleerniveau?
            if (
                (regel.minlevel && regel.minlevel > this.dlModel.effectief_modelleerniveau) ||
                (regel.maxlevel && regel.maxlevel < this.dlModel.effectief_modelleerniveau)) {
                //mag niet
                continue;
            }

            //is deze regel geaccepteerd of genegeerd?
            if (suggestiedata.rs[regel.handle] === 2) {
                continue; //geaccepteerd of genegeerd
            }
            //is het een match?
            //als eerste argument de engine zelf. Call met thisArg gaat mis in statics?
            //match is een functie of een boolean
            const matchres = typeof regel.match === 'function' ? regel.match(this, node, nodedata, def) : regel.match;
            if (matchres) {
                //deze regel is nu relevant
                this.log.debug(`Bouwsuggestie!`, nodedata, regel);
                let args: string[];
                if (matchres === true) {
                    args = [];
                } else {
                    args = Array.isArray(matchres) ? matchres : [matchres];
                }
                relevanteregels.push({
                    handle: regel.handle,
                    regel: regel,
                    args: args,
                    //`false` als vertrekpunt: `run` en `importeerRun` zetten hem daarna. Deze
                    //rondgang wordt ook los aangeroepen — door `setRegelstatus` — en dan is `false`
                    //het juiste antwoord, want hij kan een regel alleen weglaten, nooit toevoegen
                    isNieuw: false
                });
            } else {
                //als de regel wel past qua type en level maar niet qua match, dan halen we zijn regstat weg
                //zodat hij bij een eventuele nieuwe match als nieuw is
                delete suggestiedata.rs[regel.handle];
            }
        }

        //we hebben nu alle handles met args en alles. Die bewaren we. Ontkende regels zakken naar achteren, maar komen weer langs
        this.regelsPerNode.set(nodedata.id, relevanteregels);
        this.log.debug(`Relevante regels`, relevanteregels);
        //zet hem opnieuw, voor het theoretische geval dat een toekomstige versie van cytoscapejs een kopie maakt ipv een directe objectref geeft
        node.data('bs', suggestiedata);

        //we sturen geen update, dat gebeurt alleen bij
        //engine.run als alles opnieuw gaat
    }

    /**
     * Exporteert de uitkomst van de laatste run als draagbare data voor het
     * `bsSync`-samenwerkbericht: per modelnode de `bs`-data en de gematchte regels. Loopt over
     * `cy.nodes('.model')` en niet over `regelsPerNode` — die map houdt entries van verwijderde
     * nodes vast.
     */
    exporteerRun(): BsSyncNode[] {
        const res: BsSyncNode[] = [];
        for (const node of this.cy.nodes('.model').toArray()) {
            const id = node.id();
            res.push({
                id,
                bs: node.data('bs') as BsNodeData['bs'],
                regels: (this.regelsPerNode.get(id) || [])
                    .map(ri => ({handle: ri.handle, args: ri.args, isNieuw: ri.isNieuw}))
            });
        }
        return res;
    }

    /**
     * Past een geëxporteerde run van de leider toe (volgerkant van het `bsSync`-bericht): zet per
     * node de `bs`-data, herbouwt `regelsPerNode` integraal (regel-object teruggezocht op de
     * handle; onbekende handles en hier onbekende nodes worden stil overgeslagen), hertelt de
     * totalen en meldt de weergaven een run. De volger draait de engine dus nooit zelf.
     *
     * Ook `isNieuw` komt van de leider: wie de uitkomst van een ander overneemt, neemt diens
     * antwoord over. De ijklijn schuift wel mee, zodat een volger die leider wordt verder kan.
     *
     * Eén uitzondering, en dat is de replay (golf a5): een teruggehaalde run meldt per definitie geen
     * nieuws. De opslagvorm blijft daarmee gelijk aan het `bsSync`-bericht — inclusief `isNieuw` per
     * regel — en de replay zegt bij binnenkomst waar zijn data vandaan komt, in plaats van dat er een
     * vlag door de weergave moet reizen.
     * @param data de geëxporteerde run
     * @param [herkomst] waar die run vandaan komt; bij `teruggehaald` staat alles op niet-nieuw
     */
    importeerRun(data: BsSyncNode[], herkomst: Feedbackherkomst = 'vanLeider'): void {
        this.regelsPerNode.clear();
        const sleutels = new Set<string>();
        const nieuws = {noodzakelijk: false, gewenst: false};
        for (const sync of data) {
            const node = this.cy.$id(sync.id);
            if (node.empty()) {
                continue;
            }
            node.data('bs', sync.bs ?? {});
            const regelinfos: RegelInfo[] = [];
            for (const r of sync.regels) {
                const regel = this.regels.find(reg => reg.handle === r.handle);
                if (regel) {
                    const isNieuw = herkomst !== 'teruggehaald' && !!r.isNieuw;
                    regelinfos.push({handle: r.handle, regel, args: r.args, isNieuw});
                    sleutels.add(`${sync.id}|${r.handle}`);
                    if (isNieuw) {
                        nieuws[regel.soort === 'gewenst' ? 'gewenst' : 'noodzakelijk'] = true;
                    }
                }
            }
            this.regelsPerNode.set(sync.id, regelinfos);
        }
        this.vorigeRegelsleutels = sleutels;
        this._nieuwsPerSoort.set(nieuws);
        this.hertelTotalen();
        this.meldWeergavenNaRun();
    }

    /**
     * Er is een significante wijziging aan een node. Wij resetten daarom alle regels voor die node.
     * Als we opnieuw runnen (nadat de hele modelchange langskomt) heeft dat dus consequenties. Dat
     * meldde vroeger de AngularJS-modelview; op dit moment roept niemand deze methode aan.
     */
    nodeChange(node: NodeSingular): void {
        //we resetten gewoon de regels
        //vind de relevante parent-node
        const agenda: NodeSingular[] = [node];

        while (agenda.length > 0) {
            const werknode = agenda.shift() as NodeSingular;
            this.log.debug(`[nodechange] ${werknode.data('type')} ${werknode.removed() ? 'removed' : ''}`);
            //qs-elementen
            if (elements.isSubtypeOf(werknode.data('type') as string, 'quantity_space_element')) {
                //dan willen we de qs ook doen
                try {
                    const qs = this.vindParent(werknode, 'quantity_space');
                    if (qs) { //kan weg zijn als het bij delete is
                        agenda.push(qs);
                    } else {
                        this.log.debug(`[nodechange] qs niet gevonden: al verwijderd?`);
                    }
                } catch {
                    this.log.debug(`[nodechange] qs niet gevonden (al verwijderd element?)`);
                }
            }

            const suggestiedata = (werknode.data('bs') as BsNodeData['bs']) ?? {};
            delete suggestiedata.rs;
            //opnieuw schrijven voor het geval cytoscape eindelijk zo verstandig is om kopieën terug te geven
            werknode.data('bs', suggestiedata);
        }
    }

    /**
     * Een weergave vraagt om een regel te acken. Wij regelen dat verder.
     * @returns De toestand waarin de regel bij de node wordt gezet. Gewoonlijk 2 bij ack, 3 bij
     *   ontken, maar dat kan anders zijn als de regel niet ackbaar is.
     */
    setRegelstatus(node: NodeSingular | undefined, regelinfo: RegelInfo, status: 1 | 2 | 3): 1 | 2 | 3 {
        if (!node) {
            return 1;
        }
        const bs = (node.data('bs') as BsNodeData['bs']) || {};
        const rs = bs.rs || {};
        if (status === 2 && ((!regelinfo.regel.ack) || (regelinfo.regel.bevestig_gezien))) {
            //geldt als "nee"
            status = 3;
        }

        if (status === 1) {
            //gewoon weg
            delete rs[regelinfo.handle];
        } else {
            rs[regelinfo.handle] = status;
        }
        //wegschrijven, voor het geval cyto kopieën gaat teruggeven
        bs.rs = rs;
        node.data('bs', bs);
        this.log.debug(`Regelstatus`, rs);
        //opnieuw runnen
        this.runVoorNode(node);
        this.hertelTotalen();
        return status;
    }

    /* ******** Regelhelpers (in de engine) ******** */

    /**
     * Geeft true als de labels voldoende op elkaar lijken (levenshtein).
     */
    matchLabels(label1: string, label2: string): boolean {
        label1 = label1.toLowerCase();
        label2 = label2.toLowerCase();
        if (label1 === label2) {
            return true;
        }
        //anders via levenshtein
        return label1.length >= 3 && label2.length >= 3 && levenshtein(label1, label2) < 3;

        //https://stackoverflow.com/questions/18516942/fastest-general-purpose-levenshtein-javascript-implementation
        //https://github.com/gustf/js-levenshtein
        function levenshtein(s: string, t: string): number {
            if (s === t) {
                return 0;
            }
            const n = s.length, m = t.length;
            if (n === 0 || m === 0) {
                return n + m;
            }
            let x = 0, y: number, a: number, b: number, c: number, d: number, g: number, h = 0;
            const p = new Uint16Array(n);
            const u = new Uint32Array(n);
            for (y = 0; y < n;) {
                u[y] = s.charCodeAt(y);
                p[y] = ++y;
            }

            for (; (x + 3) < m; x += 4) {
                const e1 = t.charCodeAt(x);
                const e2 = t.charCodeAt(x + 1);
                const e3 = t.charCodeAt(x + 2);
                const e4 = t.charCodeAt(x + 3);
                c = x;
                b = x + 1;
                d = x + 2;
                g = x + 3;
                h = x + 4;
                for (y = 0; y < n; y++) {
                    a = p[y];
                    if (a < c || b < c) {
                        c = (a > b ? b + 1 : a + 1);
                    } else {
                        if (e1 !== u[y]) {
                            c++;
                        }
                    }

                    if (c < b || d < b) {
                        b = (c > d ? d + 1 : c + 1);
                    } else {
                        if (e2 !== u[y]) {
                            b++;
                        }
                    }

                    if (b < d || g < d) {
                        d = (b > g ? g + 1 : b + 1);
                    } else {
                        if (e3 !== u[y]) {
                            d++;
                        }
                    }

                    if (d < g || h < g) {
                        g = (d > h ? h + 1 : d + 1);
                    } else {
                        if (e4 !== u[y]) {
                            g++;
                        }
                    }
                    p[y] = h = g;
                    g = d;
                    d = b;
                    b = c;
                    c = a;
                }
            }

            for (; x < m;) {
                const e = t.charCodeAt(x);
                c = x;
                d = ++x;
                for (y = 0; y < n; y++) {
                    a = p[y];
                    if (a < c || d < c) {
                        d = (a > d ? d + 1 : a + 1);
                    } else {
                        if (e !== u[y]) {
                            d = c + 1;
                        } else {
                            d = c;
                        }
                    }
                    p[y] = d;
                    c = a;
                }
                h = d;
            }

            return h;
        }
    }

    /**
     * Vind de eerste childnode die voldoet aan de criteria.
     * @param node node of id
     * @param type
     * @param callback functie die een childnode binnenkrijgt en true of false teruggeeft
     */
    vindChild(node: NodeSingular | string, type?: string | string[], callback?: (child: NodeSingular) => boolean): NodeSingular | null {
        const typelijst = type ? (Array.isArray(type) ? type : [type]) : null;
        const nodeEl = typeof node === 'string' ? this.cy.$id(node) : node;
        for (const childId of ((nodeEl.data('childIds') as string[] | undefined) || [])) {
            const child = this.cy.$id(childId);
            let voldoet = true; //positieve blik op de wereld
            if (typelijst && (!typelijst.some(dittype => elements.isSubtypeOf(child.data('type') as string, dittype)))) {
                voldoet = false;
            }
            if (voldoet && callback) {
                voldoet = callback(child);
            }
            if (voldoet) {
                return child;
            }
            //recursief door
            const kleinkind = this.vindChild(child, type, callback);
            if (kleinkind) {
                return kleinkind;
            }
        }
        return null;
    }

    /**
     * Geeft alle (klein)kinderen van het opgegeven (super)type. Volgorde is: direct kind, daar
     * kinderen van, volgend direct kind. Maar vindt bijv. quantity-space-elementen wel op volgorde.
     * @param node node of id
     * @param type
     */
    vindChildren(node: NodeSingular | string, type: string): NodeSingular[] {
        const res: NodeSingular[] = [];
        const nodeEl = typeof node === 'string' ? this.cy.$id(node) : node;
        for (const childId of ((nodeEl.data('childIds') as string[] | undefined) || [])) {
            const child = this.cy.$id(childId);
            if (elements.isSubtypeOf(child.data('type') as string, type)) {
                res.push(child);
            }
            //recursief door
            res.push(...this.vindChildren(child, type));
        }
        return res;
    }

    /**
     * Vind de eerste parent met het opgegeven (super)type, of null als het niet lukt.
     * @param node node of id
     * @param type
     */
    vindParent(node: NodeSingular | string, type: string): NodeSingular | null {
        const nodeEl = typeof node === 'string' ? this.cy.$id(node) : node;
        const parentId = nodeEl.data('parentId') as string | undefined;
        if (!parentId) {
            return null;
        }
        const parent = this.cy.$id(parentId);
        if (elements.isSubtypeOf(parent.data('type') as string, type)) {
            return parent;
        }
        return this.vindParent(parent, type);
    }

    /**
     * Vind alle relaties met als (super)type het genoemde type waarin node de from of de to is.
     * @param node node of id
     * @param type
     * @param alsFrom
     * @param alsTo
     */
    vindRelaties(node: NodeSingular | string, type: string, alsFrom: boolean, alsTo: boolean): CollectionReturnValue {
        const id = typeof node === 'string' ? node : node.data('id') as string;
        const selectors: string[] = [];
        if (alsFrom) {
            selectors.push(`[from="${id}"]`);
        }
        if (alsTo) {
            selectors.push(`[to="${id}"]`);
        }
        return this.cy.$(selectors.join(',')).filter(
            rel =>
                elements.isSubtypeOf(rel.data('type') as string, type)
        );
    }

    /**
     * Vind alle relaties met als (super)type het genoemde type tussen de twee nodes. Als directed
     * true is alleen van node1 naar node2, anders beide kanten op.
     * @param node1 node of id
     * @param node2 node of id
     * @param type
     * @param directed
     */
    vindRelatiesTussen(node1: NodeSingular | string, node2: NodeSingular | string, type: string, directed: boolean): CollectionReturnValue {
        const id1 = typeof node1 === 'string' ? node1 : node1.data('id') as string;
        const id2 = typeof node2 === 'string' ? node2 : node2.data('id') as string;
        const selectors = [`[from="${id1}"][to="${id2}"]`];
        if (!directed) {
            selectors.push(`[from="${id2}"][to="${id1}"]`);
        }
        return this.cy.$(selectors.join(',')).filter(
            rel =>
                elements.isSubtypeOf(rel.data('type') as string, type)
        );
    }

    /**
     * Geeft true als qs1 en qs2 qua types precies corresponderen.
     */
    qsMap(qs1: NodeSingular | string, qs2: NodeSingular | string): boolean {
        const qs1els = this.vindChildren(qs1, 'quantity_space_element');
        const qs2els = this.vindChildren(qs2, 'quantity_space_element');
        //even lang en zelfde eerste element = corresponderen
        return qs1els.length === qs2els.length && qs1els[0].data('type') === qs2els[0].data('type');
    }

    /**
     * Geeft true als qs1 en qs2 qua types gespiegeld precies corresponderen.
     */
    qsSpiegel(qs1: NodeSingular | string, qs2: NodeSingular | string): boolean {
        const qs1els = this.vindChildren(qs1, 'quantity_space_element');
        const qs2els = this.vindChildren(qs2, 'quantity_space_element');
        //ze moeten even veel elementen hebben
        //maar ze moeten gespiegeld zijn qua types. Bij een oneven aantal maakt dat niet uit (de qs
        //begint dan met het type waar het mee eindigt), bij even aantal moet het wel gecheckt worden
        return qs1els.length === qs2els.length &&
            (qs1els.length % 2 === 1 || qs1els[0].data('type') !== qs2els[0].data('type'));
    }

    /* ******** Match regels ******** */

    /**
     * De regels waarmee we werken. De constructor sleutelt hier nog een beetje aan door de types
     * uit te werken. Volgorde wordt bepaald door volgorde in de file.
     */
    readonly regels: Regel[] = [
        /* **** Hoofdstuk 3 - Entity **** */
        {
            handle: 'entity_name',
            types: ['entity'],
            soort: 'hard',
            match: (engine, node, nodedata, def) => !nodedata.label?.length || nodedata.label.toLowerCase() === def.nodename.toLowerCase() || nodedata.label === engine.translateService.instant('NAAM_NOG_OPEN')
        },
        {
            handle: 'entity_definition',
            types: ['entity'],
            soort: 'gewenst',
            match: () => true,
            ack: true
        },
        {
            handle: 'entity_relevance',
            types: ['entity'],
            soort: 'gewenst',
            match: () => false, //uitgeschakeld
            ack: true
        },
        {
            handle: 'entity_name_singular',
            types: ['entity'],
            soort: 'gewenst',
            match: () => true,
            ack: true
        },
        {
            handle: 'entity_unique',
            types: ['entity'],
            soort: 'gewenst',
            match: (engine, node, nodedata) => {
                //is er een node met min of meer dezelfde naam?
                //fuzzy vergelijken
                const othernode = engine.cy.nodes('[type="entity"]').toArray().find(
                    other => other !== node &&
                        engine.matchLabels(nodedata.label as string, other.data('label') as string)
                );
                return othernode?.id() || false;
            },
            ack: true
        },
        {
            handle: 'entity_related',
            types: ['entity'],
            soort: 'noodzakelijk',
            bevestig_gezien: true,
            match: (engine, node) => {
                const id = node.id();
                //geen quantity of configuratie
                return (engine.cy.nodes(`[type = "quantity"][parentId="${id}"],[type="configuration"][from="${id}"],[type="configuration"][to="${id}"]`).length === 0);
            }
        },

        /* **** Hoofdstuk 4 - Configuratie **** */

        {
            handle: 'configuration_name',
            types: ['configuration'],
            soort: 'hard',
            match: (engine, node, nodedata, def) => ((!nodedata.label?.length) || nodedata.label.toLowerCase() === def.nodename.toLowerCase()) || nodedata.label === engine.translateService.instant('NAAM_NOG_OPEN')
        },
        //lastig om te bedenken waar het lampje moet komen... Ik doe het bij de configuraties
        {
            handle: 'configuration_unique',
            types: ['configuration'],
            soort: 'gewenst',
            match: (engine, node, nodedata) => {
                if (engine.vindRelatiesTussen(nodedata.from as string, nodedata.to as string, 'configuration', false).length > 1) {
                    return [nodedata.from as string, nodedata.to as string];
                }
                return false;
            },
            ack: false
        },
        {
            handle: 'configuration_definition',
            types: ['configuration'],
            soort: 'gewenst',
            match: () => true,
            ack: true
        },
        {
            handle: 'configuration_direction',
            types: ['configuration'],
            soort: 'gewenst',
            match: (engine, node, nodedata) => {
                //het is een configuratie, dus hij matcht. Even de args vinden
                return [nodedata.from as string, nodedata.to as string];
            },
            ack: true
        },
        {
            handle: 'configuration_name_active',
            types: ['configuration'],
            soort: 'gewenst',
            match: () => true,
            ack: true
        },
        {
            handle: 'configuration_relevance',
            types: ['configuration'],
            soort: 'gewenst',
            match: () => false, //uitgeschakeld
            ack: true
        },

        /* **** Hoofdstuk 5 - Quantity **** */

        {
            handle: 'quantity_name',
            types: ['quantity'],
            soort: 'hard',
            match: (engine, node, nodedata, def) => ((!nodedata.label?.length) || nodedata.label.toLowerCase() === def.nodename.toLowerCase()) || nodedata.label === engine.translateService.instant('NAAM_NOG_OPEN')
        },
        {
            handle: 'quantity_definition',
            types: ['quantity'],
            soort: 'gewenst',
            match: (engine, node, nodedata) => {
                //we checken even of de parent een entity is, maar dat is altijd zo (kan ook agent?)
                return engine.cy.$id(nodedata.parentId as string).data('type') === 'entity' &&
                    (nodedata.parentId as string);
            },
            ack: true
        },
        {
            handle: 'quantity_name_singular',
            types: ['quantity'],
            soort: 'gewenst',
            match: () => true,
            ack: true
        },
        //2 uniek-regels: die elkaar moeten uitsluiten
        {
            handle: 'quantity_unique',
            types: ['quantity'],
            soort: 'gewenst',
            match: (engine, node, nodedata) => {
                //vuurt als er géén node is met min of meer dezelfde naam
                const othernode = engine.cy.nodes('[type="quantity"]').toArray().find(
                    other => other !== node &&
                        engine.matchLabels(nodedata.label as string, other.data('label') as string)
                );
                if (!othernode) {
                    //we zoeken de parent en geven die als argument terug
                    return engine.cy.$id(nodedata.parentId as string).data('type') === 'entity' &&
                        (nodedata.parentId as string);
                }
                return false;
            },
            ack: true
        },
        {
            handle: 'quantity_unique_with_match',
            types: ['quantity'],
            soort: 'gewenst',
            match: (engine, node, nodedata) => {
                //vuurt als er wél een node is met min of meer dezelfde naam
                const othernode = engine.cy.nodes('[type="quantity"]').toArray().find(
                    other => other !== node &&
                        engine.matchLabels(nodedata.label as string, other.data('label') as string)
                );
                return othernode?.id() || false;
            },
            ack: true
        },
        {
            handle: 'quantity_relevance',
            types: ['quantity'],
            soort: 'gewenst',
            match: false, //uitgeschakeld
            ack: true
        },

        /**
         * Op level 2/3 moet een quantity een causal hebben (prop, dus)
         */
        {
            handle: 'quantity_causally_related_level_2_3',
            types: ['quantity'],
            soort: 'noodzakelijk',
            minlevel: 20,
            maxlevel: 39,
            ack: false,
            bevestig_gezien: true,
            match: (engine, node) => {
                return engine.vindRelaties(node, 'causal', true, true).length === 0;
            }
        },

        /* **** Hoofdstuk 5.1 - quantity (level 4+5) **** */
        //NIEUW 2026-01: quantity moet een relatie hebben (maakt niet uit welke)
        {
            handle: 'quantity_related',
            types: ['quantity'],
            soort: 'noodzakelijk',
            minlevel: 40,
            maxlevel: 59,
            ack: false,
            bevestig_gezien: false,
            match: (engine, node) => {
                //geen from of to in een relatie
                return engine.vindRelaties(node, 'relation', true, true).length === 0;
            }
        },
        //NIEUW 2026-01: calculus moet gebruikt worden (dus een relatie hebben)
        {
            handle: 'calc_related',
            types: ['calc'],
            soort: 'noodzakelijk',
            ack: false,
            bevestig_gezien: false,
            match: (engine, node, nodedata) => {
                //geen from of to in een relatie
                if (engine.vindRelaties(node, 'relation', true, true).length === 0) {
                    return [nodedata.from as string, nodedata.to as string];
                }
                return false;
            }
        },

        //@deprecated
        {
            handle: 'quantity_causally_related_level_4_5',
            types: ['quantity'],
            soort: 'gewenst',
            minlevel: 40,
            maxlevel: 59,
            ack: true,
            match: () => false, //uitgeschakeld
            xmatch: (engine, node) => {
                //geen from of to in een causal
                return engine.vindRelaties(node, 'causal', true, true).length === 0;
            }
        },

        {
            handle: 'influence_quantity_space',
            types: ['quantity'],
            soort: 'noodzakelijk',
            minlevel: 40,
            maxlevel: 59,
            ack: false, //dus berekend
            match: (engine, q1) => {
                /*
                De regel vuurt voor elke grootheid (Q1) die invloed (I verband) heeft op een andere grootheid (Q3) waarbij deze Q1 nog geen eigen waardenbereik heeft EN geen ongelijkheid met een andere grootheid (Q2) die ook een invloed heeft op Q3.
                Dit vat ik op als dat er geen andere invloed hoeft te zijn. Maar als die er is, moet er een ineq zijn.
                 */
                //q heeft geen qs
                if (engine.vindChild(q1, 'quantity_space')) {
                    return false;
                }
                //we checken onze invloeden
                const invloeden = engine.vindRelaties(q1, 'influence', true, false);
                if (!invloeden.length) {
                    return false; //we hebben geen invloeden
                }
                //er zijn invloeden. we vuren als er minstens 1 is waarbij we de invloed niet kunnen bepalen (geen andere beïnvloedende q waarmee we een ineq hebben)
                return invloeden.toArray().some(
                    influence_vanaf_q1 => {
                        //er is een invloed. deze regel vuurt TENZIJ we in elk van deze gevallen een ineq hebben met een q die ook een invloed heeft op Q3
                        const q3_id = influence_vanaf_q1.data('to') as string;
                        //Als er geen andere invloed is op dezelfde q3 die een ineq heeft met ons, is het misse boel
                        return !engine.vindRelaties(q3_id, 'influence', false, true).toArray().some(andere_influence =>
                            engine.vindRelatiesTussen(q1, andere_influence.data('from') as string, 'ineq', false).length > 0);
                    }
                );
            }
        },
        {
            handle: 'influence_quantity_space_null',
            types: ['quantity'],
            soort: 'noodzakelijk',
            minlevel: 40,
            maxlevel: 59,
            ack: false,
            match: (engine, node) => {
                //er moet een influence vanuit node gaan, en node moet een qs hebben, maar geen zero
                return engine.vindRelaties(node, 'influence', true, false).length > 0 &&
                    !!engine.vindChild(node, 'quantity_space') &&
                    (!engine.vindChild(node, 'quantity_space_point', child => {
                        engine.log.debug(`Check quantity_space_point`, child.data());
                        return !!child.data('isZero');
                    }));
            }
        },
        {
            handle: 'influence_quantity_space_value',
            types: ['quantity'],
            soort: 'noodzakelijk',
            minlevel: 40,
            maxlevel: 59,
            ack: false,
            match: (engine, node) => {
                //lijkt op de vorige, alleen er is wel een zero, maar geen interval
                return engine.vindRelaties(node, 'influence', true, false).length > 0 &&
                    !!engine.vindChild(node, 'quantity_space') &&
                    !!engine.vindChild(node, 'quantity_space_point', child => !!child.data('isZero')) &&
                    (!engine.vindChild(node, 'quantity_space_interval'));
            }
        },

        /* **** Hoofdstuk 6 - Causaliteiten (algemeen) **** */
        {
            handle: 'proportionality_positive',
            types: ['proportionality_positive'],
            soort: 'gewenst',
            ack: true,
            match: (engine, node, nodedata) => {
                return [nodedata.from as string, nodedata.to as string];
            }
        },
        {
            handle: 'proportionality_negative',
            types: ['proportionality_negative'],
            soort: 'gewenst',
            ack: true,
            match: (engine, node, nodedata) => {
                return [nodedata.from as string, nodedata.to as string];
            }
        },
        {
            handle: 'proportionality_relevance',
            types: ['proportionality'],
            soort: 'gewenst',
            ack: true,
            match: false //uitgeschakeld
        },

        /* **** Hoofdstuk 6.1 - causaliteiten level 4+ **** */

        {
            handle: 'influence_definition',
            types: ['influence'],
            soort: 'gewenst',
            minlevel: 40,
            maxlevel: 59,
            ack: true,
            match: (engine, node, nodedata) => {
                return [nodedata.from as string, nodedata.to as string];
            }
        },
        {
            handle: 'influence_positive',
            types: ['influence_positive'],
            soort: 'gewenst',
            minlevel: 40,
            maxlevel: 59,
            ack: true,
            match: (engine, node, nodedata) => {
                return [nodedata.from as string, nodedata.to as string];
            }
        },
        {
            handle: 'influence_negative',
            types: ['influence_negative'],
            soort: 'gewenst',
            minlevel: 40,
            maxlevel: 59,
            ack: true,
            match: (engine, node, nodedata) => {
                return [nodedata.from as string, nodedata.to as string];
            }
        },
        {
            handle: 'causal_type_mixture',
            types: ['quantity'],
            soort: 'noodzakelijk',
            minlevel: 40,
            maxlevel: 59,
            ack: false,
            match: (engine, node) => {
                //er gaat hier een prop heen en een inf
                return engine.vindRelaties(node, 'proportionality', false, true).length > 0 &&
                    engine.vindRelaties(node, 'influence', false, true).length > 0;
            }
        },

        /* **** Hoofdstuk 7: Quantity_space **** */
        {
            handle: 'quantity_space_constant',
            types: ['quantity_space'],
            soort: 'gewenst',
            ack: true,
            match: (engine, qs) => {
                if (engine.vindChildren(qs, 'quantity_space_element').length === 1) {
                    //als dat een punt is, geven we die terug
                    const child = engine.vindChild(qs, 'quantity_space_point');
                    if (child) {
                        return child.data('id') as string; //dus arg2
                    }
                }
                return false;
            }
        },
        {
            handle: 'quantity_space_interval',
            types: ['quantity_space'],
            soort: 'gewenst',
            ack: true,
            match: (engine, qs) => {
                if (engine.vindChildren(qs, 'quantity_space_element').length === 1) {
                    //als dat een interval is, geven we die terug
                    const child = engine.vindChild(qs, 'quantity_space_interval');
                    if (child) {
                        return child.data('id') as string; //dus arg2
                    }
                }
                return false;
            }
        },
        {
            handle: 'quantity_space_numerical',
            types: ['quantity_space'],
            soort: 'gewenst',
            ack: true,
            match: (engine, qs) => {
                //deze vuurt juist als er meer waarden zijn
                return engine.vindChildren(qs, 'quantity_space_element').length > 1;
            }
        },
        {
            handle: 'quantity_space_distinctive',
            types: ['quantity_space'],
            soort: 'gewenst',
            ack: true,
            match: (engine, qs) => {
                //deze vuurt juist als er meer waarden zijn
                return engine.vindChildren(qs, 'quantity_space_element').length > 1;
            }
        },

        /* **** Hoofdstuk 8 - Correspondence **** */
        {
            handle: 'correspondence_full',
            types: ['correspondence_qs_normal', 'correspondence_qs_directed'],
            soort: 'noodzakelijk',
            ack: false,
            match: (engine, corr, corrdata) => {
                //we vuren als de volgorde van interval/punt niet exact gelijk is
                const qs1 = engine.cy.$id(corrdata.from as string);
                const qs2 = engine.cy.$id(corrdata.to as string);

                //we gaan er maar vanuit dat er subelementen zijn (moet wel), die moeten niet corresponderen
                if (!engine.qsMap(qs1, qs2)) {
                    return [engine.vindParent(qs1, 'quantity')!.data('id') as string, engine.vindParent(qs2, 'quantity')!.data('id') as string];
                }
                return false;
            }
        },
        {
            handle: 'correspondence_inverse',
            types: ['correspondence_qs_reverse', 'correspondence_qs_directed_reverse'],
            soort: 'noodzakelijk',
            ack: false,
            match: (engine, corr, corrdata) => {
                //als correspondence_full, maar dan met gespiegelde qs
                const qs1 = engine.cy.$id(corrdata.from as string);
                const qs2 = engine.cy.$id(corrdata.to as string);

                if (!engine.qsSpiegel(qs1, qs2)) {
                    return [engine.vindParent(qs1, 'quantity')!.data('id') as string, engine.vindParent(qs2, 'quantity')!.data('id') as string];
                }
                return false;
            }
        },
        {
            handle: 'correspondence_full_bidirectional',
            types: ['correspondence_qs_normal'],
            soort: 'gewenst',
            ack: true,
            match: (engine, corr, corrdata) => {
                //deze regel vuurt juist als de qs precies mapt
                const qs1 = engine.cy.$id(corrdata.from as string);
                const qs2 = engine.cy.$id(corrdata.to as string);
                if (engine.qsMap(qs1, qs2)) {
                    return [engine.vindParent(qs1, 'quantity')!.data('id') as string, engine.vindParent(qs2, 'quantity')!.data('id') as string];
                }
                return false;
            }
        },
        {
            handle: 'correspondence_full_directed',
            types: ['correspondence_qs_directed'],
            soort: 'gewenst',
            ack: true,
            match: (engine, corr, corrdata) => {
                //deze regel vuurt juist als de qs precies mapt
                const qs1 = engine.cy.$id(corrdata.from as string);
                const qs2 = engine.cy.$id(corrdata.to as string);
                if (engine.qsMap(qs1, qs2)) {
                    return [engine.vindParent(qs1, 'quantity')!.data('id') as string, engine.vindParent(qs2, 'quantity')!.data('id') as string];
                }
                return false;
            }
        },
        {
            handle: 'correspondence_inverse_bidirectional',
            types: ['correspondence_qs_reverse'],
            soort: 'gewenst',
            ack: true,
            match: (engine, corr, corrdata) => {
                //deze regel vuurt juist als de qs precies spiegelt
                const qs1 = engine.cy.$id(corrdata.from as string);
                const qs2 = engine.cy.$id(corrdata.to as string);
                if (engine.qsSpiegel(qs1, qs2)) {
                    return [engine.vindParent(qs1, 'quantity')!.data('id') as string, engine.vindParent(qs2, 'quantity')!.data('id') as string];
                }
                return false;
            }
        },
        {
            handle: 'correspondence_inverse_directed',
            types: ['correspondence_qs_directed_reverse'],
            soort: 'gewenst',
            ack: true,
            match: (engine, corr, corrdata) => {
                //deze regel vuurt juist als de qs precies spiegelt
                const qs1 = engine.cy.$id(corrdata.from as string);
                const qs2 = engine.cy.$id(corrdata.to as string);
                if (engine.qsSpiegel(qs1, qs2)) {
                    return [engine.vindParent(qs1, 'quantity')!.data('id') as string, engine.vindParent(qs2, 'quantity')!.data('id') as string];
                }
                return false;
            }
        },
        {
            handle: 'correspondence_relevance',
            types: ['correspondence'],
            soort: 'gewenst',
            ack: true,
            match: false //uitgeschakeld
        },

        /* **** Hoofdstuk 9 - ineq **** */

        {
            handle: 'equality',
            types: ['q_ineq_eq'],
            soort: 'gewenst',
            ack: true,
            match: (engine, ineq, ineqdata) => {
                //check tussen quantity
                const q1 = engine.cy.$id(ineqdata.from as string);
                const q2 = engine.cy.$id(ineqdata.to as string);
                if (q1.data('type') === 'quantity' && q2.data('type') === 'quantity') {
                    return [q1.data('id') as string, q2.data('id') as string];
                }
                return false;
            }
        },
        {
            handle: 'in/equality',
            types: ['q_ineq_lt', 'q_ineq_lte', 'q_ineq_gt', 'q_ineq_gte'],
            soort: 'gewenst',
            ack: true,
            match: (engine, ineq, ineqdata) => {
                //check tussen quantity
                const q1 = engine.cy.$id(ineqdata.from as string);
                const q2 = engine.cy.$id(ineqdata.to as string);
                if (q1.data('type') === 'quantity' && q2.data('type') === 'quantity') {
                    return [q1.data('id') as string, q2.data('id') as string];
                }
                return false;
            }
        },
        {
            handle: 'in/equality_relevance',
            types: ['q_ineq'],
            soort: 'gewenst',
            ack: true,
            match: false //uitgeschakeld
        },
        {
            handle: 'in/equality_competing_processes',
            //deze mappen we op de 'bron' quantities
            types: ['quantity'],
            soort: 'gewenst',
            ack: false, //dus berekend
            match: (engine, q1, q1data) => {
                //voor nu geeft deze regel 1 combi terug. Als er meer zijn zie je die niet na een ack
                //todo: engine omschrijven zodat regels meerdere sets args kunnen teruggeven
                //en ook worden opgeslagen met hun args. (Je ackt dus niet in/equality_competing_processes, maar in/equality_competing_processes_q1id_q2id etc
                //er bestaat een influence die begint bij de q1, en naar q2 gaat, terwijl er een andere influence vanaf q3 naar q2 gaat en er geen ongelijkheid is tussen q en q3

                //dit lijkt heel erg op influence_quantity_space. Alleen vuurt deze regel als bij q1 een invloed op q3 is, waarbij er nóg een invloed op q3 is, zonder ongelijkheid tussen de twee beïnvloeders, en minstens 1 heeft geen qs
                engine.log.debug(`[in/equality_competing_processes] Check ${q1.data('label')}`);

                const q1_heeft_qs = !!engine.vindChild(q1, 'quantity_space');
                const q1_id = q1data.id as string;

                const invloeden = engine.vindRelaties(q1, 'influence', true, false);
                if (!invloeden.length) {
                    engine.log.debug(`[in/equality_competing_processes] Geen invloeden vanaf ${q1.data('label')} (${q1_id})`);
                    return false; //we hebben geen invloeden
                }
                //we zoeken de eerste match, ivm de argumenten
                for (const influence_vanaf_q1 of invloeden.toArray()) {
                    const q3_id = influence_vanaf_q1.data('to') as string;
                    const influences_naar_q3 = engine.vindRelaties(q3_id, 'influence', false, true);
                    //als invloeden_naar_q3 lengte 1 heeft, dan is er maar 1 invloed op q3
                    //en geldt deze regel niet
                    if (influences_naar_q3.length < 2) {
                        engine.log.debug(`[in/equality_competing_processes] Geen extra invloeden naar q3 vanaf ${q1.data('label')} (${q1_id})`);
                        continue; //nvt
                    }
                    //we zijn geïnteresseerd in de andere kant van zo'n invloed (q2)
                    //als die geen ongelijkheid heeft met q1 en q1 of die q2 heeft geen qs
                    //dan hebben we een match
                    //we geven de eerste die we vinden
                    for (const influence_naar_q3 of influences_naar_q3.toArray()) {
                        const q2_id = influence_naar_q3.data('from') as string;
                        if (q2_id === q1_id) {
                            continue; //de regel matcht niet want dit is de originele invloed
                        }
                        //als q1 en q2 een qs hebben is het niet interessant
                        if (q1_heeft_qs && (!!engine.vindChild(q2_id, 'quantity_space'))) {
                            engine.log.debug(`[in/equality_competing_processes] q1 (${q1.data('label')}) en q2 hebben een qs: niet interessant`);
                            continue;
                        }
                        engine.log.debug(`[in/equality_competing_processes] q2 of q1 (${q1.data('label')}) heeft geen qs, ineqs:`, engine.vindRelatiesTussen(q1, q2_id, 'ineq', false).length);
                        //als er geen ineq tussen q1 en q2 zit, hebben we een match
                        if (engine.vindRelatiesTussen(q1, q2_id, 'ineq', false).length === 0) {
                            //gevonden!
                            return [q2_id]; //dit is de andere
                        }
                    }
                }
                return false; //niet gevonden
            }
        },
    ];
}
